/**
 * The external dependencies
 */

/** @jsxRuntime classic */
/** @jsx jsx */
/** @jsxFrag Fragment */
import {
  GetUserDto,
  ListChangeDto,
  ListNotificationDto,
  UpdateChangeDtoStatusEnum,
  ListProjectDto,
} from '@codefluegel/zeta-change-typescript-client';
import { css, jsx } from '@emotion/react';
import {
  Box,
  Button,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';

/**
 * The internal dependencies
 */
import DashboardIcon from '@mui/icons-material/Dashboard';
import TableRowsIcon from '@mui/icons-material/TableRows';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import {
  useChangeApiFactory,
  useProjectApiFactory,
  useUserApiFactory,
} from '../ApiClient';
import ChangeRequestFilters from '../components/ChangeRequestFilters/ChangeRequestFilters';
import ChangeDataGrid from '../components/Grid/ChangeDataGrid';
import LoadSpinner from '../components/LoadSpinner/LoadSpinner';
import NavigationBar from '../components/NavigationBar/NavigationBar';
import NotificationCard from '../components/NotificationPanel/NotificationCard/NotificationCard';
import NotificationPanel from '../components/NotificationPanel/NotificationPanel';
import Section from '../components/Section/Section';
import TaskCard from '../components/TaskCard/TaskCard';
import useLocalStorage from '../hooks/useLocalStorage';
import { useChangeStore } from '../store/changeStore';
import { useProjectStore } from '../store/projectStore';
import { useStorex } from '../store/UIStore';
import { getChangeLinkByStatus, getLatestNotifications } from '../utils/utils';
import xmt_bg from '../assets/cmt_bg.png';
import { formatNumber } from '../utils/getTotalTime';

interface SearchableListChangeDto extends ListChangeDto {
  searchableString: string;
}

const TaskOverviewPage = observer(() => {
  const store = useStorex();
  const { change, resetStore, isDataLoading, setIsDataLoading } =
    useChangeStore();

  const auth = useAuth();

  const { t } = useTranslation();
  const { projects } = useProjectStore();

  const userApi = useUserApiFactory();
  const projectApi = useProjectApiFactory();
  const changeApi = useChangeApiFactory();
  const navigate = useNavigate();
  const [mode, setMode] = useLocalStorage('dashboardMode', 'dashboard');

  const [changeRequests, setChangeRequests] = useState<GetUserDto>();
  const [filteredStatuses, setFilteredStatuses] = useLocalStorage<string[]>(
    'taskOverviewFilteredStatuses',
    [],
  );
  const [filteredProjects, setFilteredProjects] = useLocalStorage<string[]>(
    'taskOverviewFilteredProjects',
    [],
  );
  const [filteredChangeTypes, setFilteredChangeTypes] = useLocalStorage<
    string[]
  >('taskOverviewFilteredChangeTypes', []);
  const [searchString, setSearchString] = useState('');
  const [notifications, setNotifications] = useState<ListNotificationDto[]>([]);
  const [allChangeRequests, setAllChangeRequests] = useState<
    SearchableListChangeDto[]
  >([]);
  const [availableProjectsFromChanges, setAvailableProjectsFromChanges] =
    useState<ListProjectDto[]>([]);
  const [activeTab, setActiveTab] = useLocalStorage('taskOverviewActiveTab', 0);
  const [isFilteringTab, setIsFilteringTab] = useState(false);
  /* debouncing of all the filters */
  const [pendingFilters, setPendingFilters] = useState({
    statuses: filteredStatuses,
    projects: filteredProjects,
    changeTypes: filteredChangeTypes,
    search: searchString,
  });
  const [appliedFilters, setAppliedFilters] = useState(pendingFilters);
  const debouncedApplyFilters = useDebouncedCallback(filters => {
    setAppliedFilters(filters);
  }, 300);

  useEffect(() => {
    setPendingFilters({
      statuses: filteredStatuses,
      projects: filteredProjects,
      changeTypes: filteredChangeTypes,
      search: searchString,
    });
  }, [filteredStatuses, filteredProjects, filteredChangeTypes, searchString]);

  useEffect(() => {
    debouncedApplyFilters(pendingFilters);
  }, [pendingFilters, debouncedApplyFilters]);
  // --- END FULL DEBOUNCE FILTERS ---

  const handleSearchStringChange = (value: string) => {
    setSearchString(value);
  };

  const filtred = useMemo(() => {
    if (!allChangeRequests) return [];
    const lowerCaseSearchString = appliedFilters.search.toLowerCase();
    const hasFilteredStatuses = appliedFilters.statuses.length > 0;
    const hasFilteredProjects = appliedFilters.projects.length > 0;
    const hasFilteredChangeTypes = appliedFilters.changeTypes.length > 0;

    // Only filter by search string if at least 3 chars or empty (to allow clearing)
    const shouldFilterBySearch =
      lowerCaseSearchString.length === 0 || lowerCaseSearchString.length >= 2;

    return allChangeRequests
      .filter((entry: SearchableListChangeDto) => {
        let matchesSearchString = true;
        if (shouldFilterBySearch) {
          if (lowerCaseSearchString) {
            matchesSearchString = entry.searchableString.includes(
              lowerCaseSearchString,
            );
          } else {
            matchesSearchString = true;
          }
        } else {
          matchesSearchString = true;
        }

        const matchesStatus = hasFilteredStatuses
          ? appliedFilters.statuses.includes(entry.status)
          : true;

        const matchesProject = hasFilteredProjects
          ? appliedFilters.projects.includes(entry.projectId?.toString() || '')
          : true;

        const isInternal = entry.preSelectInternalChange;
        const matchesChangeType = hasFilteredChangeTypes
          ? (isInternal && appliedFilters.changeTypes.includes('internal')) ||
            (!isInternal && appliedFilters.changeTypes.includes('external'))
          : true;

        return (
          matchesSearchString &&
          matchesStatus &&
          matchesProject &&
          matchesChangeType
        );
      })
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );
  }, [allChangeRequests, appliedFilters]);

  const myTasks = useMemo(() => {
    if (!changeRequests) return [];
    const userChanges = new Set(
      [
        ...changeRequests.authorChangeRequests,
        ...changeRequests.calcResponsibleChangeRequests,
        ...changeRequests.changeManagerChangeRequests,
        ...changeRequests.projectManagerChangeRequests,
        ...changeRequests.coreTeamMemberChangeRequests,
        ...changeRequests.temporaryChangeManagerChangeRequests,
        ...changeRequests.temporaryCoreTeamMemberChangeRequests,
        ...changeRequests.viewerChangeRequests,
      ].map((item: ListChangeDto) => item.id),
    );
    const rejectedStatuses = new Set<UpdateChangeDtoStatusEnum>([
      UpdateChangeDtoStatusEnum.Rejected,
      UpdateChangeDtoStatusEnum.EvalCalcRejected,
      UpdateChangeDtoStatusEnum.InternalApprovalRejected,
      UpdateChangeDtoStatusEnum.NewRejected,
    ]);
    return filtred.filter(
      (item: ListChangeDto) =>
        userChanges.has(item.id) &&
        !rejectedStatuses.has(item.status as UpdateChangeDtoStatusEnum),
    );
  }, [changeRequests, filtred]);

  const openChanges = useMemo(() => {
    if (!filtred) return [];
    return filtred.filter(
      (item: ListChangeDto) =>
        item.status !== UpdateChangeDtoStatusEnum.Closed &&
        item.status !== UpdateChangeDtoStatusEnum.Rejected &&
        item.status !== UpdateChangeDtoStatusEnum.EvalCalcRejected &&
        item.status !== UpdateChangeDtoStatusEnum.InternalApprovalRejected &&
        item.status !== UpdateChangeDtoStatusEnum.Draft &&
        item.status !== UpdateChangeDtoStatusEnum.DraftDiscarded &&
        item.status !== UpdateChangeDtoStatusEnum.Rejected &&
        item.status !== UpdateChangeDtoStatusEnum.NewRejected,
    );
  }, [filtred]);

  const closedChanges = useMemo(() => {
    if (!filtred) return [];
    return filtred.filter(
      (item: ListChangeDto) =>
        item.status === UpdateChangeDtoStatusEnum.Closed ||
        item.status === UpdateChangeDtoStatusEnum.Rejected ||
        item.status === UpdateChangeDtoStatusEnum.EvalCalcRejected ||
        item.status === UpdateChangeDtoStatusEnum.InternalApprovalRejected ||
        item.status === UpdateChangeDtoStatusEnum.NewRejected,
    );
  }, [filtred]);

  const gridChangeRequests = useMemo(() => {
    switch (activeTab) {
      case 0:
        return myTasks;
      case 1:
        return openChanges;
      case 2:
        return closedChanges;
      case 3:
        return filtred;
      default:
        return filtred;
    }
  }, [activeTab, myTasks, openChanges, closedChanges, filtred]);

  const displayData = useMemo(() => {
    if (activeTab === 0) return myTasks;
    if (activeTab === 1) return openChanges;
    if (activeTab === 2) return closedChanges;
    if (activeTab === 3) return filtred;
    return [];
  }, [activeTab, myTasks, openChanges, closedChanges, filtred]);

  useEffect(() => {
    (async () => {
      try {
        if (changeRequests) return;
        setIsDataLoading(true);
        const user = auth.user?.profile.email?.toLocaleLowerCase();
        const changes = await userApi.usersControllerFindOne({
          id: user || '',
        });
        const { data } = changes;

        const allNotifications =
          await userApi.usersControllerFindAllNotifications({
            id: user || '',
            onlyUnread: true,
          });

        const { data: notificationList } = allNotifications;

        const latestNotifications =
          getLatestNotifications(notificationList) || [];

        setNotifications(
          latestNotifications
            .sort((a, b) => Number(b.id) - Number(a.id))
            .slice(0, 5),
        );

        const { data: allC } = await changeApi.changesControllerFindAll({
          associatedUserId: user,
        });

        const allCIds = new Set(allC.map(c => c.id));
        const viewerChangesNotInAllC = (
          data?.viewerChangeRequests || []
        ).filter(vc => !allCIds.has(vc.id));

        const completeChangesList = [...allC, ...viewerChangesNotInAllC];

        const enhancedChanges = completeChangesList.map(
          (changeItem: ListChangeDto) => {
            const project = projects.find(p => p.id === changeItem.projectId);
            const searchableFields = [
              changeItem.title,
              changeItem.changeNoClient,
              changeItem.statusClient,
              changeItem.solutionProposal,
              changeItem.author,
              project?.name,
              project?.number,
            ];

            const searchableString = searchableFields
              .filter(Boolean)
              .join(' ')
              .toLowerCase();

            return { ...changeItem, searchableString };
          },
        );

        setAllChangeRequests(enhancedChanges);
        const projectIdsFromAllChanges = new Set(
          completeChangesList.map(ch => ch.projectId).filter(Boolean),
        );

        const { data: allProjects } =
          await projectApi.projectsControllerFindAll();

        const projectsFromAllChanges = allProjects.filter(project =>
          projectIdsFromAllChanges.has(project.id),
        );
        setAvailableProjectsFromChanges(projectsFromAllChanges);
        setChangeRequests(data);
        setIsDataLoading(false);
      } catch (e) {
        console.error(e);
      }
    })();

    if (change) {
      resetStore();
    }
  }, [
    auth.user?.profile.email,
    change,
    changeApi,
    changeRequests,
    projectApi,
    projects,
    resetStore,
    setIsDataLoading,
    store,
    userApi,
  ]);

  useEffect(() => {
    if (isFilteringTab) {
      setIsFilteringTab(false);
    }
  }, [activeTab, filtred, myTasks, openChanges, closedChanges, isFilteringTab]);

  const addNewChange = () => {
    navigate('/change-request/new');
  };

  const openChange = (changeId: number, status: string) => {
    const appendString = getChangeLinkByStatus(status);
    const url = `/change-request/${changeId}/${appendString}`;
    window.open(url, '_blank');
  };

  const handleChangeMode = (event: SelectChangeEvent) => {
    setMode(event.target.value);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    if (newValue !== activeTab) {
      setIsFilteringTab(true);
    }
    setActiveTab(newValue);
  };

  const totalSum = useMemo(
    () =>
      displayData
        .map(item => item.totalSalesValue)
        .filter((value): value is number => value != null)
        .reduce((sum, value) => sum + value, 0),
    [displayData],
  );

  return (
    <Box
      css={css`
        padding: 48px 24px 24px;
        flex-grow: 1;
        margin-left: auto;
        transition: width 225ms cubic-bezier(0, 0, 0.2, 1) 0ms;

        @media (max-width: 430px) {
          padding: 48px 16px 24px;
        }
      `}
      sx={{
        width: '100%',
        height: 400,
        backgroundImage: `url(${xmt_bg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <NavigationBar />

      <Box
        css={css`
          margin-bottom: 24px;
          border-bottom: 1px solid #e0e0e0;
          padding-bottom: 16px;
        `}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          textColor="secondary"
          indicatorColor="secondary"
          css={css`
            margin-bottom: 16px; /* Space between tabs and filters */
          `}
        >
          <Tab
            sx={{
              color: 'lightgrey',
              '&.Mui-selected': {
                color: 'white',
              },
            }}
            label={t('change.myTasks')}
          />
          <Tab
            sx={{
              color: 'lightgrey',
              '&.Mui-selected': {
                color: 'white',
              },
            }}
            label={t('change.openChanges')}
          />
          <Tab
            sx={{
              color: 'lightgrey',
              '&.Mui-selected': {
                color: 'white',
              },
            }}
            label={t('change.closedChanges')}
          />
          <Tab
            sx={{
              color: 'lightgrey',
              '&.Mui-selected': {
                color: 'white',
              },
            }}
            label={t('change.allChanges')}
          />
        </Tabs>

        <ChangeRequestFilters
          searchString={searchString}
          setSearchString={handleSearchStringChange}
          filteredStatuses={filteredStatuses}
          setFilteredStatuses={setFilteredStatuses}
          filteredProjects={filteredProjects}
          setFilteredProjects={setFilteredProjects}
          filteredChangeTypes={filteredChangeTypes}
          setFilteredChangeTypes={setFilteredChangeTypes}
          projects={availableProjectsFromChanges}
        />
      </Box>

      <Box
        css={css`
          display: flex;
          flex-wrap: nowrap;
          margin-top: 40px;
          @media (max-width: 768px) {
            flex-direction: column-reverse;
            align-items: center;
          }
        `}
      >
        {(isDataLoading || isFilteringTab) && <LoadSpinner />}
        {!isDataLoading && !isFilteringTab && displayData.length === 0 && (
          <Box
            css={css`
              display: flex;
              justify-content: center;
              align-items: center;
              height: 400px;
              width: 100%;
              flex: 1;
            `}
          >
            <Typography variant="h5" color="text.secondary">
              {t('dropdowns.noResultsFound')}
            </Typography>
          </Box>
        )}
        <Grid container sx={{ width: '100%' }}>
          {!isDataLoading &&
            !isFilteringTab &&
            displayData.length > 0 &&
            mode === 'dashboard' && (
              <Grid
                size={10}
                css={css`
                  @media (max-width: 900px) {
                    flex: 0 0 100%;
                    max-width: 100%;
                  }
                `}
                sx={{ marginTop: 5, borderRadius: '20px' }}
              >
                <Section
                  search={false}
                  Title={t('change.changes')}
                  headerExtraInfo={
                    <Box
                      css={css`
                        display: flex;
                        align-items: center;
                        gap: 8px;
                      `}
                    >
                      <Typography>{t('costs.totalSalesValue')}:</Typography>
                      <Typography
                        css={css`
                          font-size: 16px;
                          font-weight: 700;
                          text-transform: uppercase;
                        `}
                      >
                        € {formatNumber(totalSum ?? 0)}
                      </Typography>
                    </Box>
                  }
                  css={css`
                    margin-top: 24px;
                    width: 100%;
                    max-width: calc(100vw - 60px) !important;
                    @media (max-width: 768px) {
                      max-width: 100% !important;
                    }
                  `}
                >
                  <Grid
                    container
                    css={css`
                      display: flex;
                      align-items: center;
                      gap: 16px;
                      margin-top: 10px;
                    `}
                  >
                    {displayData.map(changeRequest => (
                      <Grid key={changeRequest.id}>
                        <TaskCard change={changeRequest} onClick={openChange} />
                      </Grid>
                    ))}
                  </Grid>
                </Section>
              </Grid>
            )}
          {!isDataLoading &&
            !isFilteringTab &&
            displayData.length > 0 &&
            mode === 'list' &&
            changeRequests && (
              <Grid
                size={10}
                css={css`
                  @media (max-width: 900px) {
                    flex: 0 0 100%;
                    max-width: 100%;
                  }
                `}
                sx={{ marginTop: 5, borderRadius: '20px' }}
              >
                <ChangeDataGrid changeRequests={gridChangeRequests} />
              </Grid>
            )}

          <Grid
            size={2}
            css={css`
              display: flex;
              margin-left: auto;
              justify-content: right;

              @media (max-width: 1200px) {
                max-width: unset;
              }

              @media (max-width: 600px) {
                width: 100%;
              }
            `}
          >
            <Grid container direction="column">
              <Grid
                css={css`
                  display: flex;
                  justify-content: flex-end;
                  align-items: flex-end;
                  gap: 16px;
                `}
              >
                <Select
                  sx={{
                    // Existing styles
                    backgroundColor: 'white',
                    color: '#00223B',
                    '&:hover': {
                      backgroundColor: '#f9fafb83',
                    },
                    // The key change to override the focused background
                    '&.Mui-focused': {
                      backgroundColor: 'white', // Example new color
                    },
                    '& .MuiSvgIcon-root': {
                      color: '#00223B',
                    },
                    '& .MuiSelect-select': {
                      paddingRight: 4,
                      paddingLeft: 1,
                      paddingTop: '5px',
                      paddingBottom: '5px',
                      color: 'white',
                    },
                  }}
                  variant="filled"
                  value={mode}
                  onChange={handleChangeMode}
                >
                  <MenuItem dense value="dashboard">
                    <DashboardIcon />
                  </MenuItem>
                  <MenuItem dense value="list">
                    <TableRowsIcon />
                  </MenuItem>
                </Select>

                <Button
                  onClick={addNewChange}
                  variant="contained"
                  sx={{
                    backgroundColor: 'white',
                    color: '#00223B',
                    '&:hover': {
                      backgroundColor: '#f9fafb83',
                    },
                  }}
                  css={css`
                    margin-top: -31px;

                    @media (max-width: 768px) {
                      margin-top: 24px;
                    }
                  `}
                >
                  + CHANGE REQUEST
                </Button>
              </Grid>

              <Grid
                css={css`
                  @media (max-width: 1200px) {
                    margin: 0px auto;
                    width: max-content;
                  }
                  @media (max-width: 480px) {
                    display: none;
                  }
                `}
              >
                {notifications.length > 0 && (
                  <NotificationPanel>
                    {notifications.map(notification => (
                      <NotificationCard
                        key={notification.id}
                        NotificationId={notification.id}
                        read={notification.read}
                        Title={notification.changeRequest.title || ''}
                        ChangeNoClient={
                          notification.changeRequest.changeNoClient || ''
                        }
                        ChangeNo={notification.changeRequest.changeNo || ''}
                        ProjectId={
                          projects.find(
                            project =>
                              project.id ===
                              notification.changeRequest.projectId,
                          )?.number || ''
                        }
                        ChangeId={notification.changeRequest.id.toString()}
                        Status={
                          (notification.changeRequest
                            .status as UpdateChangeDtoStatusEnum) || ''
                        }
                        navigateToChange={url => navigate(url)}
                      />
                    ))}
                  </NotificationPanel>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
});

export default TaskOverviewPage;
